/**
 * Wrapper functions for working with rbush with various geometric types
 * @module
 */
import RBush from "rbush";
import { RBush3D } from "rbush-3d";
import type { AABB2 } from "./aabb2Types";
import type { AABB3 } from "./aabb3Types";
import type { BRep, Face } from "./brep";
import { faceAABB3 } from "./brep";
import { DIST_EPSILON } from "./core";
import { polyLine2AABB2, polyLine2EdgeCount } from "./polyLine2";
import type { PolyLine2 } from "./polyLine2Types";

const { min, max } = Math;

export interface IndexAABB2 extends AABB2 {
  index: number;
}

export interface IndexAABB3 extends AABB3 {
  index: number;
}

export function rtreeEdgeNetwork(verts: ArrayLike<number>, edges: ArrayLike<number>) {
  // put all of the edges (expressed in 2d coordinate space) into an rtree
  const rbushItems = new Array<IndexAABB2>(edges.length / 2);
  for (let i = 0, l = edges.length; i < l; i += 2) {
    const a = edges[i];
    const b = edges[i + 1];

    const ax = verts[2 * a];
    const ay = verts[2 * a + 1];

    const bx = verts[2 * b];
    const by = verts[2 * b + 1];

    rbushItems[i / 2] = {
      minX: min(ax, bx) - DIST_EPSILON,
      minY: min(ay, by) - DIST_EPSILON,
      maxX: max(ax, bx) + DIST_EPSILON,
      maxY: max(ax, bx) + DIST_EPSILON,
      index: i / 2,
    };
  }

  const rtree = new RBush<IndexAABB2>();
  rtree.load(rbushItems);

  return rtree;
}

export function rtreeBRepFaces(brep: BRep): RBush3D {
  const faces = brep.faces;

  const rbushItems = faces.map((x, i) => {
    const aabb = faceAABB3(x);
    (aabb as IndexAABB3).index = i;
    return aabb;
  });

  const rtree = new RBush3D();
  rtree.load(rbushItems);

  return rtree;
}

export function rtreeFaceLoops(f: Face) {
  const rbushItems = f.loops.map((x, i) => {
    const aabb = polyLine2AABB2(x) as IndexAABB2;
    aabb.index = i;
    return aabb;
  });

  const rtree = new RBush<IndexAABB2>();
  rtree.load(rbushItems);

  return rtree;
}

export function rtreePolyLine2s(pls: PolyLine2[]) {
  const rbushItems = pls.map((x, i) => {
    const aabb = polyLine2AABB2(x) as IndexAABB2;
    aabb.index = i;
    return aabb;
  });

  const rtree = new RBush<IndexAABB2>();
  rtree.load(rbushItems);

  return rtree;
}

export function rtreeAddPolyLine2(rtree: RBush<IndexAABB2>, pl: PolyLine2, index: number) {
  const aabb = polyLine2AABB2(pl) as IndexAABB2;
  aabb.index = index;
  rtree.insert(aabb);
}

export interface RBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  loopIndex: number;
  edgeIndex: number;
}

export function rtreeFaceEdges(f: Face): RBush<RBox> {
  // put all of the segment ends (expressed in 2d coordinate space) into an rtree

  let edgeCount = 0;
  for (const loop of f.loops) {
    edgeCount += polyLine2EdgeCount(loop);
  }

  const rbushItems = new Array<RBox>(edgeCount);

  let count = 0;

  for (let j = 0, jl = f.loops.length; j < jl; j++) {
    const loop = f.loops[j];

    for (let i = 0, l = loop.length - 2; i < l; i += 2) {
      const ax = loop[i];
      const ay = loop[i + 1];

      const bx = loop[i + 2];
      const by = loop[i + 3];

      rbushItems[count++] = {
        minX: ax - DIST_EPSILON,
        minY: ay - DIST_EPSILON,
        maxX: bx + DIST_EPSILON,
        maxY: by + DIST_EPSILON,
        edgeIndex: i / 2,
        loopIndex: j,
      };
    }
  }

  const rtree = new RBush<RBox>();
  rtree.load(rbushItems);

  return rtree;
}
